/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Element } from 'react';

import React, { useEffect, useState } from 'react';
// eslint-disable-next-line import/extensions
import { FaCloudUploadAlt, FaRegFilePdf, FaRegLightbulb } from 'react-icons/fa';
import { Alert, Button, Col, Row } from 'reactstrap';
import Header from '#components/support/layout/Header/index.js';
import Main from '#components/support/layout/MainPageContainer/index.jsx';
import Page from '#components/support/layout/Page/index.js';
import { MODELS, PROGRESS } from '#constants/pathStrings.js';
import * as layout from '../support/reportReaderLayout.module.scss';
import * as styles from './UploadStep.module.scss';

type Props = {|
  model: ?{ modelName: ?string },
  routeParams: {| modelId: string |},
  navigate: Function,
  fetchModel: Function,
  uploadDocumentAndStartExtraction: Function,
  endCreateModelFlow: Function,
  leaveCreateModelFlowStep: Function,
|};

/*
 * Upload step (UI design §2.1) -- reachable through the UI only via the decision step's
 * "Upload a report" control (HBR-4950 #316; RequireCreateModelFlowStep gates the route). Offers
 * the PDF picker, wired to the real presigned-S3 upload -> create-job chain (data & API design
 * §2.1/§2.2), or Cancel straight to the empty model (§1) -- same destination the old Skip control
 * used, but one-shot (decision 3/5): there is no in-model "upload later" entry point afterward.
 */
const UploadStep = ({
  model,
  routeParams,
  navigate,
  fetchModel,
  uploadDocumentAndStartExtraction,
  endCreateModelFlow,
  leaveCreateModelFlowStep,
}: Props): Element<typeof Page> => {
  const { modelId } = routeParams;
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchModel(modelId);
  }, [modelId, fetchModel]);

  // Covers every exit this component doesn't itself drive -- a successful upload's own navigation
  // to /progress, browser Back, etc. (Copilot finding on PR #344): clears 'upload' so a later
  // in-session Back/Forward to this route redirects into the model instead of re-rendering it.
  useEffect(
    () => () => leaveCreateModelFlowStep(modelId, 'upload'),
    [modelId, leaveCreateModelFlowStep]
  );

  const acceptFile = candidate => {
    if (candidate && candidate.type === 'application/pdf') {
      setFile(candidate);
      setError(null);
    } else if (candidate) {
      setError('Only PDF files are supported. Please choose a PDF of the report.');
    }
  };

  const handleStartExtraction = () => {
    if (!file || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    uploadDocumentAndStartExtraction(modelId, file)
      .then(({ jobId }) => navigate(`/${MODELS}/${modelId}/${PROGRESS}`, { state: { jobId } }))
      .catch(() => {
        setSubmitting(false);
        setError("We couldn't upload this report. Please try again.");
      });
  };

  const handleCancel = () => {
    endCreateModelFlow(modelId);
    navigate(`/${MODELS}/${modelId}`);
  };

  return (
    <Page className="model-type-page">
      <Header />
      <Main className={layout.centeredMain}>
        <Row className="justify-content-center">
          <Col md="10" lg="8" xl="7" className="col text-center">
            <h1 className={layout.title}>
              SP3-AI-<em>ReportReader</em>
            </h1>
            <p className={layout.intro}>
              {model?.modelName ? (
                <>
                  Upload the structural report for <strong>{model.modelName}</strong>.
                </>
              ) : (
                'Upload a structural report.'
              )}{' '}
              SP3-AI reads the report and proposes model inputs, and you review each one before
              running the model.
            </p>
            <p className={styles.hint}>Works best with ASCE 41 Tier 1 reports.</p>
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col md="10" lg="8" xl="7" className="col">
            {error && <Alert color="danger">{error}</Alert>}
            <div
              className={`${styles.dropzone} ${isDragOver ? styles.dropzoneActive : ''}`}
              onDragOver={event => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={event => {
                event.preventDefault();
                setIsDragOver(false);
                acceptFile(event.dataTransfer.files?.[0]);
              }}
            >
              {file ? (
                <>
                  <FaRegFilePdf className={styles.icon} aria-hidden="true" />
                  <p className={styles.fileName}>{file.name}</p>
                </>
              ) : (
                <>
                  <FaCloudUploadAlt className={styles.icon} aria-hidden="true" />
                  <p className={styles.prompt}>Drag and drop the report PDF here, or</p>
                </>
              )}
              <label htmlFor="pdfUploadInput" className={styles.browseLink}>
                {file ? 'choose a different file' : 'browse your files'}
                <input
                  id="pdfUploadInput"
                  type="file"
                  accept="application/pdf"
                  className={styles.hiddenInput}
                  onChange={event => acceptFile(event.target.files?.[0])}
                />
              </label>
            </div>
            <div className={styles.actions}>
              <Button
                className="start-extraction-button"
                color="primary"
                disabled={!file || submitting}
                onClick={handleStartExtraction}
              >
                {submitting ? 'Uploading…' : 'Upload report'}
              </Button>{' '}
              <Button
                className="cancel-upload-button"
                color="link"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
            <p className={styles.disclaimer}>
              <FaRegLightbulb className={styles.disclaimerIcon} aria-hidden="true" />
              <span>
                SP3-AI gives you a fast first draft. Like any AI, it can misread a report, so every
                input it proposes is marked, shows where it came from, and waits for your review
                before the model runs.
              </span>
            </p>
          </Col>
        </Row>
      </Main>
    </Page>
  );
};

export default UploadStep;
